/**
 * FileDialogShell — the frame both file dialogs share.
 *
 *   ┌ title ─────────────────────────────────────────────┐
 *   │ sidebar     │ ← ↑  Documents › Projects   [search] │
 *   │  Workspace  │ All  Images  PDFs  Documents …       │
 *   │  Documents  │ ┌──────────────────────────┐ preview │
 *   │  Arche Saves│ │ FileList                 │ (Open)  │
 *   │  Recent     │ └──────────────────────────┘         │
 *   ├─────────────┴──────────────────────────────────────┤
 *   │ footer (mode-specific)                             │
 *   └────────────────────────────────────────────────────┘
 *
 * Below `md` the dialog is full-screen and the sidebar folds into two
 * menus (workspace, location) above the list.
 *
 * Built on the Radix dialog, so focus is trapped while open, Escape cancels,
 * and focus returns to the control that opened it. My-Fias never appears.
 */
import { useRef, type ReactNode } from 'react';
import {
  ArrowLeft,
  ArrowUp,
  ChevronRight,
  Clock,
  FolderOpen,
  LayoutGrid,
  List,
  Search,
  Sparkles,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { VaultFileDialogDocumentDto } from '@/lib/api';
import { VAULT_FILE_KIND_LABELS, type VaultFileKind } from '@/lib/vault-file-kinds';
import { FileList, type FileListEntry } from './FileList';
import { ARCHE_SAVES_HOME, DOCUMENTS_ROOT, RECENT, sameLocation } from './locations';
import type { FileDialogSort } from './prefs';
import type { FileBrowser } from './useFileBrowser';

/** The chips offered, in order (the plan's set; Code / Archives / Presentations
 *  fold into "Other" visually but stay filterable through `accept`). */
const CHIP_KINDS: readonly VaultFileKind[] = [
  'image',
  'pdf',
  'document',
  'spreadsheet',
  'audio',
  'video',
  'other',
];

const SORT_OPTIONS: Array<{ value: string; label: string; sort: FileDialogSort }> = [
  {
    value: 'updated:desc',
    label: 'Modified — newest',
    sort: { sort: 'updated', direction: 'desc' },
  },
  { value: 'updated:asc', label: 'Modified — oldest', sort: { sort: 'updated', direction: 'asc' } },
  { value: 'name:asc', label: 'Name — A to Z', sort: { sort: 'name', direction: 'asc' } },
  { value: 'name:desc', label: 'Name — Z to A', sort: { sort: 'name', direction: 'desc' } },
  { value: 'size:desc', label: 'Size — largest', sort: { sort: 'size', direction: 'desc' } },
  { value: 'size:asc', label: 'Size — smallest', sort: { sort: 'size', direction: 'asc' } },
  { value: 'kind:asc', label: 'Type', sort: { sort: 'kind', direction: 'asc' } },
];

export interface FileDialogShellProps {
  open: boolean;
  /** Called with `false` on Escape / overlay / close — unless `busy`. */
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  browser: FileBrowser;
  /** Offer Arche Saves in the sidebar (Open). Save browses Documents only. */
  showArcheSaves: boolean;
  /** Offer Recent in the sidebar. */
  showRecent?: boolean;
  /** Kinds the chips may offer (the requester's accepted types). */
  chipKinds?: readonly VaultFileKind[];
  selectedIds: ReadonlySet<string>;
  multiSelect: boolean;
  onSelectFile: (file: VaultFileDialogDocumentDto, opts: { toggle: boolean }) => void;
  onActivateFile: (file: VaultFileDialogDocumentDto) => void;
  onFocusEntry?: (entry: FileListEntry | null) => void;
  /** Buttons beside the search box (New folder, Upload). */
  toolbarExtra?: ReactNode;
  /** Ctrl/Cmd+Shift+N — create a folder, where the mode allows it. */
  onNewFolder?: () => void;
  /** Shown above the list (a notice, an inline new-folder field). */
  banner?: ReactNode;
  /** The right-hand pane (Open's preview), hidden below `lg`. */
  aside?: ReactNode;
  footer: ReactNode;
  /** Work in flight — the dialog cannot be dismissed. */
  busy?: boolean;
  /** Extra line for an empty folder ("Upload a file to get started."). */
  emptyHint?: string;
  /** Workspace switching is locked (a tray holds files of one workspace). */
  workspaceLockedReason?: string | null;
  /** Render inline, inside a surface's own modal, instead of as a dialog. */
  embedded?: boolean;
}

export function FileDialogShell(props: FileDialogShellProps) {
  const { browser } = props;
  const searchRef = useRef<HTMLInputElement>(null);
  const showRecent = props.showRecent !== false;
  const chipKinds = props.chipKinds?.length
    ? CHIP_KINDS.filter((k) => props.chipKinds!.includes(k))
    : CHIP_KINDS;

  const onKeyDown = (e: React.KeyboardEvent) => {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && !e.shiftKey && e.key.toLowerCase() === 'f') {
      e.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    } else if (mod && e.shiftKey && e.key.toLowerCase() === 'n' && props.onNewFolder) {
      e.preventDefault();
      props.onNewFolder();
    } else if (e.altKey && e.key === 'ArrowLeft' && browser.canGoBack) {
      e.preventDefault();
      browser.back();
    }
  };

  const status = statusMessage(browser);

  const locationButtons: Array<{
    key: string;
    label: string;
    icon: ReactNode;
    go: () => void;
    active: boolean;
  }> = [
    {
      key: 'documents',
      label: 'Documents',
      icon: <FolderOpen className="h-4 w-4" aria-hidden="true" />,
      go: () => browser.navigate(DOCUMENTS_ROOT),
      active: browser.location.kind === 'folder' && browser.location.area === 'documents',
    },
    ...(props.showArcheSaves
      ? [
          {
            key: 'arche-saves',
            label: 'Arche Saves',
            icon: <Sparkles className="h-4 w-4" aria-hidden="true" />,
            go: () => browser.navigate(ARCHE_SAVES_HOME),
            active: browser.location.kind === 'folder' && browser.location.area === 'arche-saves',
          },
        ]
      : []),
    ...(showRecent
      ? [
          {
            key: 'recent',
            label: 'Recent',
            icon: <Clock className="h-4 w-4" aria-hidden="true" />,
            go: () => browser.navigate(RECENT),
            active: sameLocation(browser.location, RECENT),
          },
        ]
      : []),
  ];
  const activeLocationKey = locationButtons.find((b) => b.active)?.key ?? 'documents';

  const workspaceSelect = (id: string) => (
    <select
      id={id}
      className="h-10 w-full rounded-md border bg-background px-2 text-sm disabled:opacity-60"
      value={browser.holderId ?? ''}
      disabled={browser.workspaces.length <= 1 || !!props.workspaceLockedReason}
      title={props.workspaceLockedReason ?? undefined}
      onChange={(e) => browser.setHolderId(e.target.value)}
    >
      {browser.workspaces.map((w) => (
        <option key={w.holderId} value={w.holderId}>
          {w.label}
        </option>
      ))}
    </select>
  );

  const body = (
    <>
      <div className="flex min-h-0 flex-1">
        <nav
          aria-label="Locations"
          className="hidden w-52 shrink-0 flex-col gap-3 border-r bg-muted/20 p-3 md:flex"
        >
          <div className="space-y-1">
            <label
              htmlFor="file-dialog-workspace"
              className="text-xs font-medium text-muted-foreground"
            >
              Workspace
            </label>
            {workspaceSelect('file-dialog-workspace')}
            {props.workspaceLockedReason && (
              <p className="text-xs text-muted-foreground">{props.workspaceLockedReason}</p>
            )}
          </div>
          <ul className="space-y-1">
            {locationButtons.map((b) => (
              <li key={b.key}>
                <button
                  type="button"
                  onClick={b.go}
                  aria-current={b.active ? 'page' : undefined}
                  className={cn(
                    'flex min-h-[40px] w-full items-center gap-2 rounded-md px-2 text-left text-sm hover:bg-muted',
                    b.active && 'bg-muted font-medium',
                  )}
                >
                  {b.icon}
                  {b.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Phone width: the sidebar folds into two menus. */}
          <div className="flex gap-2 border-b p-2 md:hidden">
            <div className="min-w-0 flex-1">
              <label htmlFor="file-dialog-workspace-sm" className="sr-only">
                Workspace
              </label>
              {workspaceSelect('file-dialog-workspace-sm')}
            </div>
            <div className="min-w-0 flex-1">
              <label htmlFor="file-dialog-location-sm" className="sr-only">
                Location
              </label>
              <select
                id="file-dialog-location-sm"
                className="h-10 w-full rounded-md border bg-background px-2 text-sm"
                value={activeLocationKey}
                onChange={(e) => locationButtons.find((b) => b.key === e.target.value)?.go()}
              >
                {locationButtons.map((b) => (
                  <option key={b.key} value={b.key}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b p-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10"
              onClick={browser.back}
              disabled={!browser.canGoBack}
              aria-label="Back"
              title="Back (Alt+←)"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10"
              onClick={browser.up}
              disabled={!browser.canGoUp}
              aria-label="Up one level"
              title="Up (Backspace)"
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
            </Button>
            {/* A floor on the path: with the Save dialog's extra toolbar button
                it was squeezed to a clipped "Documer" before anything wrapped. */}
            <nav aria-label="Folder path" className="min-w-[7rem] flex-1">
              {browser.isSearching ? (
                <span className="text-sm text-muted-foreground">Search results</span>
              ) : (
                <ol className="flex min-w-0 items-center gap-1 overflow-x-auto text-sm">
                  {browser.crumbs.map((crumb, i) => {
                    const last = i === browser.crumbs.length - 1;
                    return (
                      <li key={`${i}-${crumb.label}`} className="flex shrink-0 items-center gap-1">
                        {i > 0 && (
                          <ChevronRight
                            className="h-3.5 w-3.5 text-muted-foreground"
                            aria-hidden="true"
                          />
                        )}
                        {last ? (
                          <span
                            aria-current="location"
                            className="max-w-[14rem] truncate font-medium"
                          >
                            {crumb.label}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="max-w-[10rem] truncate text-muted-foreground hover:text-foreground"
                            onClick={() => browser.navigate(crumb.location)}
                          >
                            {crumb.label}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </nav>
            <div className="relative w-full sm:w-auto sm:min-w-[9rem] sm:max-w-[14rem] sm:flex-1">
              <Search
                className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                ref={searchRef}
                type="search"
                value={browser.search}
                onChange={(e) => browser.setSearch(e.target.value)}
                placeholder="Search"
                aria-label="Search files by name"
                className="h-10 pl-8"
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && browser.search) {
                    // Clear first; a second Escape closes the dialog.
                    e.stopPropagation();
                    browser.setSearch('');
                  }
                }}
              />
            </div>
            {props.toolbarExtra}
            <div className="flex items-center gap-1">
              <Button
                variant={browser.view === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-10 w-10"
                aria-label="List view"
                aria-pressed={browser.view === 'list'}
                onClick={() => browser.setView('list')}
              >
                <List className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant={browser.view === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-10 w-10"
                aria-label="Grid view"
                aria-pressed={browser.view === 'grid'}
                onClick={() => browser.setView('grid')}
              >
                <LayoutGrid className="h-4 w-4" aria-hidden="true" />
              </Button>
              <label htmlFor="file-dialog-sort" className="sr-only">
                Sort by
              </label>
              <select
                id="file-dialog-sort"
                className="h-10 rounded-md border bg-background px-2 text-sm"
                value={`${browser.sort.sort}:${browser.sort.direction}`}
                onChange={(e) => {
                  const option = SORT_OPTIONS.find((o) => o.value === e.target.value);
                  if (option) browser.setSort(option.sort);
                }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            role="group"
            aria-label="File types"
            className="flex gap-1 overflow-x-auto border-b px-2 py-1.5"
          >
            <TypeChip
              label="All"
              pressed={browser.kinds.length === 0}
              onClick={() => browser.setKinds([])}
            />
            {chipKinds.map((kind) => (
              <TypeChip
                key={kind}
                label={VAULT_FILE_KIND_LABELS[kind]}
                pressed={browser.kinds.includes(kind)}
                onClick={() =>
                  browser.setKinds(
                    browser.kinds.includes(kind)
                      ? browser.kinds.filter((k) => k !== kind)
                      : [...browser.kinds, kind],
                  )
                }
              />
            ))}
          </div>

          {props.banner}

          <p className="sr-only" role="status" aria-live="polite">
            {status}
          </p>

          <div className="flex min-h-0 flex-1">
            <div className="flex min-w-0 flex-1 flex-col">
              <ListingBody {...props} />
            </div>
            {props.aside && (
              <aside
                aria-label="Preview"
                className="hidden w-64 shrink-0 overflow-y-auto border-l p-3 lg:block"
              >
                {props.aside}
              </aside>
            )}
          </div>
        </div>
      </div>

      <div className="border-t px-4 py-3">{props.footer}</div>
    </>
  );

  if (props.embedded) {
    // Inside another surface's own modal (Social Media's media picker):
    // the same browser and footer, without a second dialog around them.
    return (
      <div
        className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border"
        onKeyDown={onKeyDown}
      >
        {body}
      </div>
    );
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next && props.busy) return;
        props.onOpenChange(next);
      }}
    >
      <DialogContent
        className="flex h-[100dvh] max-w-none flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[85vh] sm:max-w-5xl sm:rounded-lg"
        onKeyDown={onKeyDown}
      >
        <DialogHeader className="border-b px-4 py-3 pr-12 text-left">
          <DialogTitle className="truncate">{props.title}</DialogTitle>
          {props.description ? (
            <DialogDescription>{props.description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              Choose a location, then use the arrow keys to move through its files.
            </DialogDescription>
          )}
        </DialogHeader>

        {body}
      </DialogContent>
    </Dialog>
  );
}

function TypeChip(props: { label: string; pressed: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={props.pressed}
      onClick={props.onClick}
      className={cn(
        'min-h-[32px] shrink-0 rounded-full border px-3 text-xs',
        props.pressed ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted',
      )}
    >
      {props.label}
    </button>
  );
}

function ListingBody(props: FileDialogShellProps) {
  const { browser } = props;
  const { listing } = browser;
  if (browser.workspacesLoading || browser.foldersLoading || listing.isLoading) {
    return (
      <div className="space-y-2 p-3" aria-busy="true" aria-label="Loading files">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 rounded" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-1/2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!browser.holderId) {
    return <Empty>You don’t have a workspace you can use here.</Empty>;
  }
  if (listing.isError) {
    return (
      <Empty>
        <span>
          Couldn’t load files. {listing.error instanceof Error ? listing.error.message : ''}
        </span>
        <Button variant="outline" size="sm" onClick={() => void listing.refetch()}>
          Try again
        </Button>
      </Empty>
    );
  }
  if (browser.entries.length === 0) {
    return (
      <Empty>
        {browser.archeSavesMissing ? (
          'Nothing has been saved here yet.'
        ) : browser.isSearching ? (
          `No files match “${browser.search.trim()}”.`
        ) : browser.kinds.length > 0 ? (
          'No files of that type here.'
        ) : browser.location.kind === 'recent' ? (
          'No recent files.'
        ) : browser.location.kind === 'folder' && browser.location.area === 'arche-saves' ? (
          'Nothing has been saved here yet.'
        ) : (
          <>
            <span>This folder is empty.</span>
            {props.emptyHint && <span>{props.emptyHint}</span>}
          </>
        )}
      </Empty>
    );
  }
  const where = browser.isSearching
    ? 'Search results'
    : browser.crumbs.map((c) => c.label).join(' / ');
  return (
    <FileList
      entries={browser.entries}
      view={browser.view}
      showLocation={browser.showLocation}
      selectedIds={props.selectedIds}
      multiSelect={props.multiSelect}
      thumbnails={browser.thumbnails}
      label={`Files in ${where}`}
      onOpenFolder={(folder) =>
        browser.navigate({
          kind: 'folder',
          area: browser.location.kind === 'folder' ? browser.location.area : 'documents',
          folderId: folder.folderId,
        })
      }
      onSelectFile={props.onSelectFile}
      onActivateFile={props.onActivateFile}
      onFocusEntry={props.onFocusEntry}
      onUp={browser.up}
      hasMore={listing.hasMore}
      isLoadingMore={listing.isLoadingMore}
      onLoadMore={listing.loadMore}
    />
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

/** What the live region announces: result counts and failures. The region
 *  is polite, so it speaks when this text changes and not on every render. */
function statusMessage(browser: FileBrowser): string {
  const { listing } = browser;
  if (listing.isLoading) return 'Loading files';
  if (listing.isError) return 'Couldn’t load files';
  const count = `${listing.total} ${listing.total === 1 ? 'file' : 'files'}`;
  return browser.isSearching ? `${count} matching ${browser.search.trim()}` : count;
}
